import { useState } from 'react';
import { Grid, MenuItem, TextField } from '@mui/material';
import type { DeviceColor, DeviceModelOption } from '@/features/operations/types';

/**
 * Brand and model of a device, picked from the catalogue (#186) rather than typed: the colours
 * offered for the phone are the ones of its model, so the model has to be a catalogue entry.
 *
 * The brand only narrows the model list; what is saved is the model, and the backend reads the
 * brand from it. Retired models are offered too, flagged: PhoneTec receives phones of models it
 * no longer quotes, the same as with brands.
 *
 * Renders two grid items, so each form keeps its own layout around them.
 */
export function CatalogueModelFields({
  options,
  phoneModelId,
  onChange,
  emptyModelLabel = 'Elegí un modelo',
}: {
  options: DeviceModelOption[];
  phoneModelId: string;
  onChange: (phoneModelId: string) => void;
  /// What the model select says while nothing is chosen. A legacy device whose model matched no
  /// catalogue entry shows its old text here.
  emptyModelLabel?: string;
}) {
  const [brandId, setBrandId] = useState(
    () => options.find((option) => option.id === phoneModelId)?.brandId ?? '',
  );

  const brands = options.reduce<Array<{ id: string; name: string }>>((list, option) => {
    if (!list.some((brand) => brand.id === option.brandId)) {
      list.push({ id: option.brandId, name: option.brand });
    }
    return list;
  }, []);
  const models = options.filter((option) => option.brandId === brandId);

  return (
    <>
      <Grid item xs={12} sm={6}>
        <TextField
          select
          fullWidth
          size="small"
          label="Marca"
          value={brandId}
          SelectProps={{ displayEmpty: true }}
          InputLabelProps={{ shrink: true }}
          onChange={(event) => {
            setBrandId(event.target.value);
            // A model of another brand cannot stay chosen.
            onChange('');
          }}
        >
          <MenuItem value="">Elegí una marca</MenuItem>
          {brands.map((brand) => (
            <MenuItem key={brand.id} value={brand.id}>
              {brand.name}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          select
          fullWidth
          size="small"
          label="Modelo"
          value={phoneModelId}
          SelectProps={{ displayEmpty: true }}
          InputLabelProps={{ shrink: true }}
          disabled={!brandId}
          onChange={(event) => onChange(event.target.value)}
        >
          <MenuItem value="">{emptyModelLabel}</MenuItem>
          {models.map((option) => (
            <MenuItem key={option.id} value={option.id}>
              {option.isActive ? option.model : `${option.model} (retirado)`}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
    </>
  );
}

/**
 * The colour of a device, among the active colours of its model (#186).
 *
 * `currentColor` is the colour the device already has. It stays offered even if it was
 * deactivated or removed from the model since, flagged, because the phone is still that colour:
 * editing something else must not force anybody to change it. It is only offered while the
 * device stays the model it was received as.
 */
export function ColorField({
  options,
  phoneModelId,
  colorId,
  onChange,
  currentColor = null,
  currentPhoneModelId = null,
}: {
  options: DeviceModelOption[];
  phoneModelId: string;
  colorId: string;
  onChange: (colorId: string) => void;
  currentColor?: DeviceColor | null;
  currentPhoneModelId?: string | null;
}) {
  const phoneModel = options.find((option) => option.id === phoneModelId);
  const colors: Array<{ id: string; label: string }> = (phoneModel?.colors ?? []).map((color) => ({
    id: color.id,
    label: color.name,
  }));

  if (
    currentColor &&
    phoneModelId === currentPhoneModelId &&
    !colors.some((color) => color.id === currentColor.id)
  ) {
    colors.push({ id: currentColor.id, label: `${currentColor.name} (ya no se ofrece)` });
  }

  const helperText = !phoneModel
    ? 'Elegí primero el modelo.'
    : phoneModel.colors.length === 0
      ? 'Este modelo no tiene colores cargados. Se asignan en Administración → Modelos.'
      : undefined;

  return (
    <TextField
      select
      fullWidth
      size="small"
      label="Color"
      value={colorId}
      SelectProps={{ displayEmpty: true }}
      InputLabelProps={{ shrink: true }}
      disabled={!phoneModel}
      helperText={helperText}
      onChange={(event) => onChange(event.target.value)}
    >
      <MenuItem value="">Sin color</MenuItem>
      {colors.map((color) => (
        <MenuItem key={color.id} value={color.id}>
          {color.label}
        </MenuItem>
      ))}
    </TextField>
  );
}
